import React from 'react';
import { Box, Typography } from '@mui/material';
import DestinationCarouselCard from './DestinationCarouselCard';
import { CAROUSEL_EAGER_COUNT } from '../common/yovoImageSources';
import {
  HOMEPAGE_CAROUSEL_INITIAL_WINDOW,
  HOMEPAGE_CAROUSEL_LOADING_LABEL,
  HOMEPAGE_CAROUSEL_LOADING_SX,
} from './homepageCarousel.constants';
import { carouselSpacerWidths, visibleCarouselRange } from './homepageCarouselWindow';

/**
 * Renders only the carousel cards near the horizontal scroll position.
 * Spacers keep the scroll width the same as a full list.
 */
class WindowedCarouselTrack extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      start: 0,
      end: HOMEPAGE_CAROUSEL_INITIAL_WINDOW,
      cardWidth: 0,
      gap: 0,
    };
    this.scroller = null;
  }

  componentDidMount() {
    this.scroller = document.getElementById(this.props.trackId);
    if (!this.scroller) return;
    this.scroller.addEventListener('scroll', this.updateWindow, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateWindow);
      this.resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this.updateWindow);
    }
    this.updateWindow();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) this.updateWindow();
  }

  componentWillUnmount() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this.updateWindow);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.updateWindow);
  }

  updateWindow = () => {
    const scroller = this.scroller;
    if (!scroller) return;
    const card = scroller.querySelector('[data-carousel-card]');
    const cardWidth = card ? card.getBoundingClientRect().width : 0;
    const rawGap = getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap || '0';
    const gap = parseFloat(String(rawGap).trim().split(/\s+/)[0]) || 0;
    const range = visibleCarouselRange({
      itemCount: this.props.items?.length || 0,
      scrollLeft: scroller.scrollLeft,
      viewportWidth: scroller.clientWidth,
      cardWidth,
      gap,
    });
    this.setState((prev) => {
      if (
        prev.start === range.start
        && prev.end === range.end
        && prev.cardWidth === cardWidth
        && prev.gap === gap
      ) {
        return null;
      }
      return { start: range.start, end: range.end, cardWidth, gap };
    });
  };

  render() {
    const { items, layout, onOpen } = this.props;
    if (!items || items.length === 0) {
      return (
        <Box sx={HOMEPAGE_CAROUSEL_LOADING_SX}>
          <Typography>{HOMEPAGE_CAROUSEL_LOADING_LABEL}</Typography>
        </Box>
      );
    }

    const { start, end, cardWidth, gap } = this.state;
    const visible = items.slice(start, end);
    const { lead, tail } = carouselSpacerWidths({
      start,
      end,
      itemCount: items.length,
      cardWidth,
      gap,
    });

    return (
      <>
        {lead > 0 ? (
          <Box data-carousel-spacer aria-hidden sx={{ flex: '0 0 auto', width: `${lead}px`, alignSelf: 'stretch' }} />
        ) : null}
        {visible.map((destination, offset) => {
          const index = start + offset;
          return (
            <DestinationCarouselCard
              key={`${destination.destination || 'destination'}-${index}`}
              destination={destination}
              eager={index < CAROUSEL_EAGER_COUNT}
              layout={layout}
              onOpen={onOpen}
            />
          );
        })}
        {tail > 0 ? (
          <Box data-carousel-spacer aria-hidden sx={{ flex: '0 0 auto', width: `${tail}px`, alignSelf: 'stretch' }} />
        ) : null}
      </>
    );
  }
}

export default WindowedCarouselTrack;
